"use client";

import { ReactNode, useEffect, useState } from "react";
import { PiQuestionFill } from "react-icons/pi";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/accordion/accordion";
import { trackAnalyticsApiEvent } from "@/lib/analytics";

export type Faq = {
  question: string;
  answer: ReactNode | ReactNode[];
};

type FaqSectionProps = {
  questions: Faq[];
  title?: ReactNode;
};

export default function FaqSection(props: FaqSectionProps) {
  const [isMounted, setIsMounted] = useState(false);
  const [, setOpenQuestions] = useState<string[]>([]);
  const title = props.title ?? (
    <>
      Frequently Asked <span className="text-secondary-200 font-bold">Questions</span>
    </>
  );

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const handleValueChange = (values: string[] | string) => {
    const nextValues = Array.isArray(values) ? values : values ? [values] : [];
    setOpenQuestions(prev => {
      nextValues.forEach(value => {
        if (!prev.includes(value)) {
          trackAnalyticsApiEvent("faq_expanded", { question: value });
        }
      });
      return nextValues;
    });
  };

  const renderAnswer = (answer: ReactNode | ReactNode[]) => {
    if (!Array.isArray(answer)) {
      return <div>{answer}</div>;
    }

    return answer.map((line, idx, array) => {
      if (line === array[array.length - 1]) {
        return <div key={`line-${idx}`}>{line}</div>;
      }

      return (
        <div key={`line-${idx}`}>
          {line}
          <br />
          <br />
        </div>
      );
    });
  };

  return (
    <section className="bg-primary-50 py-8 px-4">
      <div className="w-full max-w-screen-lg mx-auto flex flex-col gap-5 xl:gap-10">
        <h2 className="max-w-xl mx-auto text-center text-balance font-medium">{title}</h2>
        {isMounted ? (
          <Accordion type="multiple" onValueChange={handleValueChange}>
            {props.questions.map(faq => (
              <AccordionItem key={faq.question} value={faq.question}>
                <AccordionTrigger className="text-lg text-start text-secondary-500">
                  <div className="flex items-center gap-4">
                    <div className="bg-secondary-50 rounded-full p-2">
                      <PiQuestionFill className="w-5 h-5 shrink-0" />
                    </div>
                    {faq.question}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-base">{renderAnswer(faq.answer)}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        ) : (
          <div className="flex flex-col border-t border-black">
            {props.questions.map(faq => (
              <div key={faq.question} className="border-b border-black p-4">
                <div className="text-lg text-start text-secondary-500 flex items-center gap-4">
                  <div className="bg-secondary-50 rounded-full p-2">
                    <PiQuestionFill className="w-5 h-5 shrink-0" />
                  </div>
                  {faq.question}
                </div>
                <div className="text-base text-gray-600 pt-3">{renderAnswer(faq.answer)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
